<template>
  <v-app-bar app
    flat
    height="68"
    class="routebus-topbar"
  >
    <div class="topbar-layout">
      <div class="topbar-left">
        <v-btn
          icon
          variant="text"
          size="38"
          class="topbar-icon-btn"
          aria-label="Toggle sidebar"
          @click="$emit('toggle-sidebar')"
        >
          <v-icon size="23">
            {{ drawer ? "mdi-menu-open" : "mdi-menu" }}
          </v-icon>
        </v-btn>

        <h2 class="page-title">
          {{ $route.meta.title || "Dashboard" }}
        </h2>
      </div>

      <div
        ref="searchContainer"
        class="topbar-search-wrapper"
        @keydown.esc="closeSearch"
      >
        <div
          class="topbar-search"
          :class="{ 'search-active': showSearchResults }"
        >
          <v-icon size="20" class="search-icon">
            mdi-magnify
          </v-icon>

          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search menus and pages..."
            aria-label="Search menus and pages"
            autocomplete="off"
            @input="onSearchInput"
            @focus="openSearch"
            @keydown.enter.prevent="handleSearch"
            @keydown.down.prevent="moveSearchSelection(1)"
            @keydown.up.prevent="moveSearchSelection(-1)"
          />

          <button
            v-if="searchQuery"
            type="button"
            class="search-clear"
            aria-label="Clear search"
            @click="clearSearch"
          >
            <v-icon size="17">mdi-close</v-icon>
          </button>

          <span v-else class="search-shortcut">
            Enter ↵
          </span>
        </div>

        <div
          v-if="showSearchResults"
          class="search-dropdown"
        >
          <div class="search-dropdown-header">
            <span>MENU NAVIGATION</span>
            <span>{{ filteredMenus.length }} results</span>
          </div>

          <div
            v-if="filteredMenus.length"
            class="search-results"
          >
            <button
              v-for="(item, index) in filteredMenus"
              :key="`${item.route}-${index}`"
              type="button"
              class="search-result"
              :class="{
                'search-result-active': selectedSearchIndex === index
              }"
              @mouseenter="selectedSearchIndex = index"
              @click="navigateToMenu(item.route)"
            >
              <div class="search-result-icon">
                <v-icon size="19">{{ item.icon }}</v-icon>
              </div>

              <div class="search-result-content">
                <span class="search-result-title">
                  {{ item.label }}
                </span>

                <span class="search-result-path">
                  Home
                  <template v-if="item.parent">
                    / {{ item.parent }}
                  </template>
                  / {{ item.label }}
                </span>
              </div>

              <v-icon size="17" class="search-result-arrow">
                mdi-arrow-up-right
              </v-icon>
            </button>
          </div>

          <div v-else class="search-empty">
            <v-icon size="27">mdi-magnify-close</v-icon>
            <span>No matching menus found</span>
          </div>

          <div class="search-dropdown-footer">
            <span>↑ ↓ Navigate</span>
            <span>Enter to open</span>
            <span>Esc to close</span>
          </div>
        </div>
      </div>

      <div class="topbar-right">
        <v-menu
          v-model="themeMenu"
          location="bottom end"
          :offset="12"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              variant="text"
              color="default"
              size="40"
              class="topbar-icon-btn"
              aria-label="Theme settings"
            >
              <v-icon size="22">mdi-cog-outline</v-icon>
            </v-btn>
          </template>

          <v-card
            width="240"
            rounded="lg"
            elevation="4"
            class="theme-settings-card"
          >
            <div class="theme-settings-title">
              Theme Mode
            </div>

            <div class="theme-settings-options">
              <v-btn
                size="small"
                variant="tonal"
                :color="!isDark ? 'primary' : 'secondary'"
                :class="{ 'theme-option-selected': !isDark }"
                @click="setThemeMode('light')"
              >
                <v-icon start size="18">
                  mdi-white-balance-sunny
                </v-icon>
                Light
              </v-btn>

              <v-btn
                size="small"
                variant="tonal"
                :color="isDark ? 'primary' : 'secondary'"
                :class="{ 'theme-option-selected': isDark }"
                @click="setThemeMode('dark')"
              >
                <v-icon start size="18">mdi-weather-night</v-icon>
                Dark
              </v-btn>
            </div>
          </v-card>
        </v-menu>

        <v-menu
          v-model="notificationMenu"
          location="bottom end"
          :offset="12"
          :close-on-content-click="false"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              variant="text"
              size="40"
              class="topbar-icon-btn"
              aria-label="Notifications"
            >
              <v-badge
                :content="unreadNotifications"
                color="error"
                floating
                :model-value="unreadNotifications > 0"
              >
                <v-icon size="22">mdi-bell-outline</v-icon>
              </v-badge>
            </v-btn>
          </template>

          <v-card
            width="380"
            rounded="lg"
            elevation="4"
            class="notification-card"
          >
            <div class="notification-header">
              <div>
                <h3>Notifications</h3>
                <span>
                  {{ unreadNotifications }} unread notifications
                </span>
              </div>

              <v-btn
                v-if="unreadNotifications"
                variant="text"
                size="small"
                color="primary"
                @click="markAllAsRead"
              >
                Mark all read
              </v-btn>
            </div>

            <v-divider />

            <div class="notification-list">
              <div
                v-for="notification in notifications"
                :key="notification.id"
                class="notification-item"
                :class="{
                  'notification-unread': !notification.read
                }"
                @click="openNotification(notification)"
              >
                <div
                  class="notification-icon"
                  :class="notification.type"
                >
                  <v-icon size="19">
                    {{ notification.icon }}
                  </v-icon>
                </div>

                <div class="notification-content">
                  <div class="notification-title-row">
                    <h4>{{ notification.title }}</h4>
                    <span class="notification-time">
                      {{ notification.time }}
                    </span>
                  </div>

                  <p>{{ notification.message }}</p>

                  <span class="notification-reference">
                    {{ notification.reference }}
                  </span>
                </div>

                <span
                  v-if="!notification.read"
                  class="unread-dot"
                />
              </div>

              <div
                v-if="!notifications.length"
                class="notification-empty"
              >
                <v-icon size="32">mdi-bell-off-outline</v-icon>
                <p>No notifications</p>
              </div>
            </div>

            <v-divider />

            <div class="notification-footer">
              <v-btn
                block
                variant="text"
                color="primary"
                size="small"
                @click="viewAllNotifications"
              >
                View all notifications
                <v-icon end size="17">mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-menu>

        <div class="topbar-divider" />

        <v-menu
          v-model="menu"
          location="bottom end"
          :offset="10"
          transition="scale-transition"
        >
          <template #activator="{ props }">
            <div
              v-bind="props"
              class="profile-trigger"
              role="button"
              tabindex="0"
              @keydown.enter.prevent="menu = !menu"
              @keydown.space.prevent="menu = !menu"
            >
              <v-avatar size="36" class="profile-avatar">
                {{ adminInitials }}
              </v-avatar>

              <div class="profile-info">
                <span class="profile-name">{{ adminName }}</span>
                <span class="profile-role">{{ adminRole }}</span>
              </div>

              <v-icon size="18" class="profile-chevron">
                mdi-chevron-down
              </v-icon>
            </div>
          </template>

          <v-card
            width="300"
            rounded="lg"
            elevation="4"
            class="rb-profile-menu"
          >
            <div class="rb-profile-header">
              <v-avatar size="42" class="rb-profile-avatar">
                {{ adminInitials }}
              </v-avatar>

              <div class="rb-profile-identity">
                <div class="rb-profile-name">
                  {{ adminName }}
                </div>
                <div class="rb-profile-role">
                  {{ adminRole }}
                </div>
              </div>
            </div>

            <div class="rb-profile-divider" />

            <div class="rb-profile-details">
              <div class="rb-profile-detail">
                <v-icon size="17">mdi-email-outline</v-icon>
                <span :title="adminEmail">{{ adminEmail }}</span>
              </div>

              <div class="rb-profile-detail">
                <v-icon size="17">mdi-phone-outline</v-icon>
                <span>{{ adminPhone }}</span>
              </div>
            </div>

            <div class="rb-profile-divider" />

            <div class="rb-profile-footer">
              <v-btn
                block
                variant="flat"
                color="primary"
                prepend-icon="mdi-logout"
                class="rb-profile-logout"
                @click="handleLogout"
              >
                Logout
              </v-btn>
            </div>
          </v-card>
        </v-menu>
      </div>
    </div>
  </v-app-bar>
</template>

<script>
import { mapActions } from "pinia";
import { useLoginStore } from "@/modules/auth/store/login";
import vuetify from "@/plugins/vuetify";
import { MENUS, USER_ROLES } from "@/shared/resource/constants";

export default {
  name: "TopBar",

  emits: ["toggle-sidebar", "search"],

  props: {
    drawer: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      menu: false,
      notificationMenu: false,
      themeMenu: false,
      searchQuery: "",
      showSearchResults: false,
      selectedSearchIndex: -1,

      notifications: [
        {
          id: 1,
          title: "New booking received",
          message: "A new booking has been confirmed.",
          type: "booking",
          reference: "Booking #BK10245",
          time: "2 min ago",
          read: false,
          icon: "mdi-ticket-confirmation-outline",
        },
        {
          id: 2,
          title: "Trip schedule updated",
          message: "Departure time has been changed.",
          type: "trip",
          reference: "Trip #TRP1021",
          time: "18 min ago",
          read: false,
          icon: "mdi-bus-clock",
        },
        {
          id: 3,
          title: "Payment received",
          message: "Payment of ₹1,250 has been successfully received.",
          type: "payment",
          reference: "Payment #PAY4521",
          time: "35 min ago",
          read: true,
          icon: "mdi-cash-check",
        },
        {
          id: 4,
          title: "New customer registered",
          message: "A new customer has created an account.",
          type: "customer",
          reference: "Customer #CUS1023",
          time: "1 hour ago",
          read: true,
          icon: "mdi-account-plus-outline",
        },
        {
          id: 5,
          title: "Trip cancelled",
          message: "Chennai → Coimbatore trip was cancelled.",
          type: "trip",
          reference: "Trip #TRP1008",
          time: "2 hours ago",
          read: false,
          icon: "mdi-bus-alert",
        },
      ],
    };
  },

  computed: {
    isDark() {
      return vuetify.theme.global.current.value?.dark ?? false;
    },

    adminInitials() {
      const name = this.$auth?.name || "Admin";

      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase();
    },

    adminName() {
      return this.$auth?.name || "Admin";
    },

    adminRole() {
      return this.$auth?.role || "Administrator";
    },

    adminEmail() {
      return this.$auth?.email || "No Email";
    },

    adminPhone() {
      return this.$auth?.phone_number || "No Phone";
    },

    unreadNotifications() {
      return this.notifications.filter(
        (notification) => !notification.read
      ).length;
    },

    availableMenus() {
      const menus =
        this.$auth?.role === USER_ROLES.SUPERADMIN
          ? MENUS.superAdmin
          : MENUS.admin;

      const results = [];

      const addMenus = (items, parent = "") => {
        (items || []).forEach((item) => {
          if (item.route) {
            results.push({
              label: item.label,
              route: item.route,
              icon: item.icon || "mdi-file-outline",
              parent,
            });
          }

          if (item.children?.length) {
            addMenus(item.children, item.label);
          }
        });
      };

      addMenus(menus);

      return results;
    },

    filteredMenus() {
      const query = this.searchQuery.trim().toLowerCase();

      if (!query) {
        return this.availableMenus.slice(0, 8);
      }

      return this.availableMenus
        .filter((item) => {
          const text = `${item.label} ${item.parent}`.toLowerCase();
          return text.includes(query);
        })
        .sort((a, b) => {
          const aStarts = a.label.toLowerCase().startsWith(query);
          const bStarts = b.label.toLowerCase().startsWith(query);

          return Number(bStarts) - Number(aStarts);
        })
        .slice(0, 12);
    },
  },

  mounted() {
    const savedTheme = localStorage.getItem("routebus-theme");

    if (savedTheme === "light" || savedTheme === "dark") {
      vuetify.theme.global.name.value = savedTheme;
    }

    document.addEventListener("mousedown", this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  },

  methods: {
    ...mapActions(useLoginStore, ["logout"]),

    setThemeMode(theme) {
      if (theme !== "light" && theme !== "dark") return;

      vuetify.theme.global.name.value = theme;
      localStorage.setItem("routebus-theme", theme);
      this.themeMenu = false;
    },

    onSearchInput() {
      this.selectedSearchIndex = -1;
      this.showSearchResults = true;
    },

    openSearch() {
      this.showSearchResults = true;
    },

    closeSearch() {
      this.showSearchResults = false;
      this.selectedSearchIndex = -1;
    },

    clearSearch() {
      this.searchQuery = "";
      this.selectedSearchIndex = -1;
      this.showSearchResults = true;
    },

    handleOutsideClick(event) {
      const container = this.$refs.searchContainer;

      if (container && !container.contains(event.target)) {
        this.closeSearch();
      }
    },

    moveSearchSelection(direction) {
      if (!this.showSearchResults) {
        this.showSearchResults = true;
      }

      const length = this.filteredMenus.length;

      if (!length) return;

      if (direction > 0) {
        this.selectedSearchIndex =
          (this.selectedSearchIndex + 1) % length;
      } else {
        this.selectedSearchIndex =
          this.selectedSearchIndex <= 0
            ? length - 1
            : this.selectedSearchIndex - 1;
      }
    },

    handleSearch() {
      if (!this.filteredMenus.length) return;

      const index =
        this.selectedSearchIndex >= 0
          ? this.selectedSearchIndex
          : 0;

      const selectedMenu = this.filteredMenus[index];

      if (selectedMenu?.route) {
        this.navigateToMenu(selectedMenu.route);
      }
    },

    navigateToMenu(route) {
      if (!route) return;

      this.searchQuery = "";
      this.closeSearch();

      if (this.$route.path !== route) {
        this.$router.push(route).catch((error) => {
          console.error("Navigation failed:", error);
        });
      }
    },

    async handleLogout() {
      await this.logout();
      this.menu = false;
      this.$router.push("/login");
    },

    openNotification(notification) {
      notification.read = true;
      this.notificationMenu = false;
    },

    markAllAsRead() {
      this.notifications.forEach((notification) => {
        notification.read = true;
      });
    },

    viewAllNotifications() {
      this.notificationMenu = false;
      this.$router.push("/notifications");
    },
  },
};
</script>

<style scoped>
.routebus-topbar {
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
  border-bottom: 1px solid rgb(var(--v-theme-rb-border)) !important;
  box-shadow: 0 2px 12px rgba(15, 35, 70, 0.05) !important;
  overflow: visible !important;
}

.topbar-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 0 24px;
  gap: 18px;
}

.topbar-left,
.topbar-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.topbar-left {
  gap: 12px;
  min-width: 0;
}

.topbar-right {
  gap: 12px;
}

.topbar-icon-btn {
  color: rgb(var(--v-theme-rb-text)) !important;
  border-radius: 9px !important;
}

.topbar-icon-btn:hover {
  background: rgb(var(--v-theme-rb-primary-light)) !important;
  color: rgb(var(--v-theme-rb-sidebar-active-text)) !important;
}

.page-title {
  margin: 0;
  color: rgb(var(--v-theme-rb-text));
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
}

.topbar-search-wrapper {
  position: relative;
  flex: 0 1 460px;
  width: 100%;
  min-width: 180px;
  z-index: 20;
}

.topbar-search {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 42px;
  padding: 0 13px;
  background: rgb(var(--v-theme-surface-variant));
  border: 1px solid rgb(var(--v-theme-rb-border));
  border-radius: 10px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.topbar-search:focus-within,
.topbar-search.search-active {
  background: rgb(var(--v-theme-surface));
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 3px rgba(255, 176, 0, 0.12);
}

.search-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-rb-muted));
}

.search-input {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  color: rgb(var(--v-theme-rb-text));
  font-size: 13px;
  font-weight: 600;
}

.search-input::placeholder {
  color: rgb(var(--v-theme-rb-muted));
  font-weight: 500;
  opacity: 1;
}

.search-shortcut {
  flex-shrink: 0;
  padding: 4px 7px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-rb-border));
  border-radius: 5px;
  color: rgb(var(--v-theme-rb-muted));
  font-size: 10px;
  font-weight: 600;
}

.search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  flex-shrink: 0;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: rgb(var(--v-theme-rb-muted));
  cursor: pointer;
}

.search-clear:hover {
  background: rgb(var(--v-theme-rb-primary-light));
  color: rgb(var(--v-theme-rb-sidebar-active-text));
}

.search-dropdown {
  position: absolute;
  top: calc(100% + 9px);
  left: 0;
  width: 100%;
  min-width: 300px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-rb-border));
  border-radius: 12px;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  z-index: 1000;
}

.search-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 15px;
  border-bottom: 1px solid rgb(var(--v-theme-rb-border));
  color: rgb(var(--v-theme-rb-muted));
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.search-results {
  max-height: 340px;
  overflow-y: auto;
  padding: 6px;
  scrollbar-width: thin;
}

.search-result {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 58px;
  padding: 9px 11px;
  border: none;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.search-result:hover,
.search-result-active {
  background: rgb(var(--v-theme-rb-sidebar-hover));
}

.search-result-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 9px;
  background: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-rb-muted));
}

.search-result:hover .search-result-icon,
.search-result-active .search-result-icon {
  background: rgb(var(--v-theme-rb-primary-light));
  color: rgb(var(--v-theme-rb-sidebar-active-text));
}

.search-result-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.search-result-title {
  color: rgb(var(--v-theme-rb-text));
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}

.search-result-path {
  color: rgb(var(--v-theme-rb-muted));
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-result-arrow {
  color: rgb(var(--v-theme-rb-muted));
}

.search-result:hover .search-result-arrow {
  color: rgb(var(--v-theme-primary));
}

.search-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 30px 15px;
  color: rgb(var(--v-theme-rb-muted));
  font-size: 12px;
  font-weight: 600;
}

.search-dropdown-footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 11px 15px;
  background: rgb(var(--v-theme-surface-variant));
  border-top: 1px solid rgb(var(--v-theme-rb-border));
  color: rgb(var(--v-theme-rb-muted));
  font-size: 10px;
  font-weight: 600;
}

.topbar-divider {
  width: 1px;
  height: 30px;
  background: rgb(var(--v-theme-rb-border));
}

.theme-settings-card {
  padding: 16px;
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
  border: 1px solid rgb(var(--v-theme-rb-border));
}

.theme-settings-title {
  margin-bottom: 12px;
  color: rgb(var(--v-theme-rb-text));
  font-size: 13px;
  font-weight: 700;
}

.theme-settings-options {
  display: flex;
  gap: 8px;
}

.theme-settings-options .v-btn {
  flex: 1;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.theme-option-selected {
  border: 1px solid rgb(var(--v-theme-primary));
}

.profile-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.profile-trigger:hover {
  background: rgb(var(--v-theme-rb-sidebar-hover));
}

.profile-avatar,
.rb-profile-avatar {
  background: rgb(var(--v-theme-rb-primary-light)) !important;
  color: rgb(var(--v-theme-rb-sidebar-active-text)) !important;
  font-size: 13px;
  font-weight: 800;
}

.profile-info,
.rb-profile-identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.profile-name {
  color: rgb(var(--v-theme-rb-text));
  font-size: 13px;
  font-weight: 800;
  line-height: 1.4;
}

.profile-role {
  color: rgb(var(--v-theme-rb-muted));
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  text-transform: capitalize;
}

.profile-chevron {
  margin-left: 3px;
  color: rgb(var(--v-theme-rb-muted));
}

.rb-profile-menu {
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
  border: 1px solid rgb(var(--v-theme-rb-border)) !important;
  border-radius: 14px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16) !important;
  overflow: hidden;
}

.rb-profile-header {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 15px 16px;
}

.rb-profile-avatar {
  flex-shrink: 0;
  font-size: 15px;
}

.rb-profile-identity {
  gap: 3px;
}

.rb-profile-name {
  color: rgb(var(--v-theme-on-surface));
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rb-profile-role {
  color: rgb(var(--v-theme-rb-muted));
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  text-transform: capitalize;
}

.rb-profile-divider {
  height: 1px;
  background: rgb(var(--v-theme-rb-border));
}

.rb-profile-details {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 9px 10px;
}

.rb-profile-detail {
  display: flex;
  align-items: center;
  gap: 11px;
  min-height: 37px;
  padding: 7px 9px;
  border-radius: 8px;
  color: rgb(var(--v-theme-rb-text));
  transition: background-color 0.2s ease;
}

.rb-profile-detail:hover {
  background: rgb(var(--v-theme-rb-sidebar-hover));
}

.rb-profile-detail .v-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-rb-muted));
}

.rb-profile-detail span {
  min-width: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.rb-profile-footer {
  padding: 11px 12px;
}

.rb-profile-logout {
  min-height: 37px !important;
  border-radius: 9px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.notification-card {
  overflow: hidden;
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
  border: 1px solid rgb(var(--v-theme-rb-border));
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.15) !important;
}

.notification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
}

.notification-header h3 {
  margin: 0;
  color: rgb(var(--v-theme-rb-text));
  font-size: 14px;
  font-weight: 700;
}

.notification-header span {
  color: rgb(var(--v-theme-rb-muted));
  font-size: 11px;
  font-weight: 500;
}

.notification-list {
  max-height: 390px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  position: relative;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-rb-border));
  cursor: pointer;
  transition: background 0.2s;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item:hover,
.notification-unread {
  background: rgb(var(--v-theme-rb-sidebar-hover));
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  height: 36px;
  border-radius: 9px;
  background: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-rb-text));
}

.notification-icon.booking {
  color: rgb(var(--v-theme-info));
}

.notification-icon.trip {
  color: rgb(var(--v-theme-warning));
}

.notification-icon.payment {
  color: rgb(var(--v-theme-success));
}

.notification-icon.customer {
  color: rgb(var(--v-theme-primary));
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.notification-content h4 {
  margin: 0;
  color: rgb(var(--v-theme-rb-text));
  font-size: 12px;
  font-weight: 700;
}

.notification-content p {
  margin: 5px 0;
  color: rgb(var(--v-theme-rb-muted));
  font-size: 11px;
  line-height: 1.5;
}

.notification-time {
  color: rgb(var(--v-theme-rb-muted));
  font-size: 10px;
  white-space: nowrap;
}

.notification-reference {
  color: rgb(var(--v-theme-rb-sidebar-active-text));
  font-size: 10px;
  font-weight: 700;
}

.unread-dot {
  position: absolute;
  top: 18px;
  right: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
}

.notification-empty {
  padding: 40px 20px;
  color: rgb(var(--v-theme-rb-muted));
  text-align: center;
}

.notification-empty p {
  margin: 8px 0 0;
  font-size: 12px;
}

.notification-footer {
  padding: 8px;
}

@media (max-width: 1100px) {
  .topbar-layout {
    padding: 0 16px;
    gap: 12px;
  }

  .topbar-search-wrapper {
    flex-basis: 340px;
  }

  .search-shortcut {
    display: none;
  }
}

@media (max-width: 768px) {
  .topbar-layout {
    padding: 0 12px;
    gap: 8px;
  }

  .page-title {
    display: none;
  }

  .topbar-search-wrapper {
    flex: 1;
    min-width: 0;
  }

  .topbar-search {
    padding: 0 10px;
  }

  .topbar-right {
    gap: 6px;
  }

  .profile-info,
  .profile-chevron,
  .topbar-divider {
    display: none;
  }

  .profile-trigger {
    padding: 4px;
  }

  .search-dropdown {
    min-width: min(340px, calc(100vw - 24px));
  }
}

@media (max-width: 480px) {
  .topbar-layout {
    padding: 0 8px;
    gap: 4px;
  }

  .topbar-search {
    height: 38px;
  }

  .search-input {
    font-size: 12px;
  }

  .search-input::placeholder {
    font-size: 11px;
  }

  .search-dropdown-footer {
    font-size: 9px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .topbar-search,
  .search-result,
  .profile-trigger,
  .rb-profile-detail,
  .notification-item {
    transition: none;
  }
}
</style>
